import type {
  BindingContextTypeDef,
  TypeReferenceDef,
} from '@packages/systems/dynamo/source-types';

type TypeIdentifier = string;
type TypeReferenceMode = 'internal' | 'external';
type IconName = 'collection' | 'product' | 'category' | 'cart' | 'search';

/** Internal types */

export type BindingContextType = {
  _kind: 'BindingContextType';
  readonly entity: 'Collection' | 'Field' | 'Entity' | 'Root' | 'PageRoot';
  readonly id: TypeIdentifier;
  readonly label: string;
  readonly singularLabel: string;
  readonly pluralLabel: string;
  readonly iconName: IconName;
  readonly notImplemented: boolean;
  readonly externalKey: string | null;
};

export type BindingContextTypeReference =
  | ListTypeReference
  | EntityTypeReference
  | RootTypeReference;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type ListTypeReference = {
  _kind: 'BindingContextTypeReference';
  entity: 'List';
  ofEntity: EntityTypeReference;
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type EntityTypeReference = {
  _kind: 'BindingContextTypeReference';
  entity: 'Collection' | 'Field' | 'Entity' | 'PageRoot';
  id: TypeIdentifier;
  mode: TypeReferenceMode;
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type RootTypeReference = {
  _kind: 'BindingContextTypeReference';
  entity: 'Root';
  id: 'root';
};

/** Constructors for internal types */

const ROOT_TYPE_ID = 'root';

export function buildBindingContextType(
  definition: BindingContextTypeDef
): BindingContextType {
  const isRootBindingContextDef = definition.entity === 'Root';

  const id = isRootBindingContextDef ? ROOT_TYPE_ID : definition.id;
  const singularLabel = isRootBindingContextDef
    ? null
    : definition.singularLabel;
  const pluralLabel = isRootBindingContextDef ? null : definition.pluralLabel;
  const iconName = isRootBindingContextDef ? null : definition.iconName;
  const externalKey = isRootBindingContextDef ? null : definition.externalKey;

  return {
    _kind: 'BindingContextType',
    entity: definition.entity,
    label: definition.label,
    singularLabel: singularLabel || '',
    pluralLabel: pluralLabel || '',
    iconName: iconName || 'collection',
    notImplemented: isRootBindingContextDef
      ? false
      : Boolean(definition.notImplemented),
    id,
    externalKey: externalKey || null,
  };
}

export function buildInternalTypeReference(
  definition: TypeReferenceDef
): EntityTypeReference {
  const {entity, id} = definition;
  return {_kind: 'BindingContextTypeReference', entity, id, mode: 'internal'};
}

export function buildExternalTypeReference(
  definition: TypeReferenceDef
): EntityTypeReference {
  const {entity, id} = definition;
  return {_kind: 'BindingContextTypeReference', entity, id, mode: 'external'};
}

export function buildListTypeReference(
  type: EntityTypeReference
): ListTypeReference {
  return {
    _kind: 'BindingContextTypeReference',
    entity: 'List',
    ofEntity: type,
  };
}

export function buildRootTypeReference(): RootTypeReference {
  return {
    _kind: 'BindingContextTypeReference',
    entity: 'Root',
    id: ROOT_TYPE_ID,
  };
}

/** Utilities for handling internal types */

export function toTypeReference(
  type: BindingContextType
): BindingContextTypeReference {
  if (type.entity === 'Root') {
    return buildRootTypeReference();
  }
  return buildInternalTypeReference({
    entity: type.entity,
    id: type.id,
  });
}

export function getEntityTypeReference(
  typeReference: BindingContextTypeReference
) {
  return typeReference.entity === 'List'
    ? typeReference.ofEntity
    : typeReference;
}

export function isPageRootTypeReference(
  typeReference: BindingContextTypeReference
) {
  return typeReference.entity === 'PageRoot';
}

export function areReferencesToSameEntity(
  typeReferenceA: BindingContextTypeReference,
  typeReferenceB: BindingContextTypeReference
): boolean {
  const entityA = getEntityTypeReference(typeReferenceA);
  const entityB = getEntityTypeReference(typeReferenceB);

  if (entityA.entity === 'Root' || entityB.entity === 'Root') {
    // TODO (dynamo infra): once type references identify the gateway,
    // we can match root references
    return false;
  }

  // TODO (dynamo infra): also check that gateways match, once type references
  // identify the gateway
  return entityA.entity === entityB.entity && entityA.id === entityB.id;
}

/** Type reference builder */

export interface TypeReferenceBuilderAPI {
  root(): RootTypeReference;
  listOf(arg1: TypeReferenceDef): ListTypeReference;
  entityOf(arg1: TypeReferenceDef): EntityTypeReference;
}

export class TypeReferenceBuilder implements TypeReferenceBuilderAPI {
  typeMap: EntityMapping<BindingContextType>;

  constructor(typeMap: EntityMapping<BindingContextType>) {
    this.typeMap = typeMap;
  }

  entityOf(typeReferenceDef: TypeReferenceDef) {
    return this.typeMap.contains(typeReferenceDef)
      ? buildInternalTypeReference(typeReferenceDef)
      : buildExternalTypeReference(typeReferenceDef);
  }

  listOf(typeReferenceDef: TypeReferenceDef) {
    return buildListTypeReference(this.entityOf(typeReferenceDef));
  }

  root() {
    return buildRootTypeReference();
  }
}

/** Mapping of type references to values */

type EntityMap<Value> = Partial<
  Record<
    'Collection' | 'Field' | 'Entity' | 'Root' | 'PageRoot',
    Partial<Record<TypeIdentifier, Value>>
  >
>;

export class EntityMapping<Value> {
  map: EntityMap<Value>;

  constructor() {
    this.map = {};
  }

  get(typeRef: BindingContextTypeReference): Value | null | undefined {
    const {entity, id} = getEntityTypeReference(typeRef);
    return this.map[entity]?.[id];
  }

  set(typeRef: BindingContextTypeReference, value: Value): void {
    const {entity, id} = getEntityTypeReference(typeRef);
    const existingEntityMap = this.map[entity];

    if (existingEntityMap) {
      existingEntityMap[id] = value;
    } else {
      this.map[entity] = {[id]: value};
    }
  }

  contains(typeReferenceDefinition: TypeReferenceDef): boolean {
    const {entity, id} = typeReferenceDefinition;
    return this.map[entity]?.[id] != null;
  }
}
